/**
 * BackgroundSelector — "Apariencia" control for Settings/Profile (issue #414).
 *
 * Lets the user pick how the dashboard background behaves:
 *  - Automático por sección (default): follows the sidebar section.
 *  - Animado: Remotion pulse loop everywhere.
 *  - Sólido: flat navy, safest/fastest.
 *  - Fijar imagen: pin one section image everywhere (reveals a secondary
 *    image picker).
 *
 * Reads/writes via BackgroundPreferenceContext (provided by the enclosing
 * <DashboardLayout>), so the choice is reflected instantly on this same
 * page and persisted to localStorage for every other page.
 *
 * @module components/dashboard/BackgroundSelector
 */

import React from 'react';
import { Row, Col } from 'react-bootstrap';
import { useBackgroundPreferenceContext } from '../../contexts/BackgroundPreferenceContext';
import { BACKGROUND_MODES } from '../../hooks/useBackgroundPreference';
import './BackgroundSelector.css';

const MODE_PREVIEW_THUMB = {
  auto: null, // rendered as a 4-color section swatch
  animated: `${process.env.PUBLIC_URL}/images/backgrounds/bg-technical-thumb.jpg`,
  solid: null, // rendered as a flat navy swatch
  image: `${process.env.PUBLIC_URL}/images/backgrounds/bg-technical-thumb.jpg`,
};

const IMAGE_THUMB = {
  cleanroom: `${process.env.PUBLIC_URL}/images/backgrounds/bg-cleanroom-thumb.jpg`,
  comercial: `${process.env.PUBLIC_URL}/images/backgrounds/bg-comercial-thumb.jpg`,
  almacen: `${process.env.PUBLIC_URL}/images/backgrounds/bg-almacen-thumb.jpg`,
  technical: `${process.env.PUBLIC_URL}/images/backgrounds/bg-technical-thumb.jpg`,
};

const BackgroundSelector = () => {
  const { preference, setMode, setFixedImageId, modeOptions, imageOptions } = useBackgroundPreferenceContext();

  return (
    <div className="background-selector">
      <Row className="g-3" role="radiogroup" aria-label="Modo de fondo del panel">
        {modeOptions.map((option) => {
          const isSelected = preference.mode === option.id;
          const thumb = MODE_PREVIEW_THUMB[option.id];
          return (
            <Col xs={6} md={3} key={option.id}>
              <button
                type="button"
                role="radio"
                aria-checked={isSelected}
                className={`background-option-card ${isSelected ? 'is-selected' : ''}`}
                onClick={() => setMode(option.id)}
                data-testid={`background-mode-${option.id}`}
              >
                <span
                  className={`background-option-thumb background-option-thumb--${option.id}`}
                  style={thumb ? { backgroundImage: `url(${thumb})` } : undefined}
                >
                  {option.id === BACKGROUND_MODES.AUTO && (
                    <span className="background-option-thumb-auto" aria-hidden="true">
                      <span className="swatch swatch--cleanroom" />
                      <span className="swatch swatch--comercial" />
                      <span className="swatch swatch--almacen" />
                      <span className="swatch swatch--technical" />
                    </span>
                  )}
                  {option.id === BACKGROUND_MODES.ANIMATED && (
                    <span className="background-option-thumb-pulse" aria-hidden="true" />
                  )}
                  {isSelected && (
                    <span className="background-option-check" aria-hidden="true">
                      <i className="bi bi-check-circle-fill"></i>
                    </span>
                  )}
                </span>
                <span className="background-option-label">{option.label}</span>
                <span className="background-option-description">{option.description}</span>
              </button>
            </Col>
          );
        })}
      </Row>

      {preference.mode === BACKGROUND_MODES.IMAGE && (
        <div className="background-image-picker">
          <p className="background-image-picker-label">Elegí la imagen a fijar:</p>
          <Row className="g-3" role="radiogroup" aria-label="Imagen fija de fondo">
            {imageOptions.map((option) => {
              const isSelected = preference.fixedImageId === option.id;
              return (
                <Col xs={6} md={3} key={option.id}>
                  <button
                    type="button"
                    role="radio"
                    aria-checked={isSelected}
                    className={`background-option-card ${isSelected ? 'is-selected' : ''}`}
                    onClick={() => setFixedImageId(option.id)}
                    data-testid={`background-image-${option.id}`}
                  >
                    <span
                      className="background-option-thumb"
                      style={{ backgroundImage: `url(${IMAGE_THUMB[option.id]})` }}
                    >
                      {isSelected && (
                        <span className="background-option-check" aria-hidden="true">
                          <i className="bi bi-check-circle-fill"></i>
                        </span>
                      )}
                    </span>
                    <span className="background-option-label">{option.label}</span>
                    <span className="background-option-description">{option.description}</span>
                  </button>
                </Col>
              );
            })}
          </Row>
        </div>
      )}
    </div>
  );
};

export default BackgroundSelector;
